<template>
  <div>
    <section class="hero" :class="[isGhost ? 'is-dark' : 'is-primary']">
      <header class="navbar">
        <div class="container">
          <div class="navbar-brand">
            <a class="navbar-item" :href="url">
              <img :src="`${this.url}/assets/logo2.png`" alt="Logo">
            </a>
            <span class="navbar-burger" :class="[isActiveNavbarMenu ? 'is-active' : '']"
              @click="activeNavbarMenu">
              <span></span>
              <span></span>
              <span></span>
            </span>
          </div>
          <div class="navbar-menu" :class="[isActiveNavbarMenu ? 'is-active' : '']"
            @click="activeNavbarMenu">
            <div class="navbar-end">
              <span class="navbar-item" v-if="roomId">
                <VDropdown
                  :triggers="['hover', 'focus', 'touch']"
                  :autoHide="true"
                >
                  <template #popper>
                    <span class="is-size-7 has-text-link">{{ copyFeedback ? 'コピーしました！' : '共有用URLをクリップボードにコピーします。' }}</span><br />
                    <button class="button is-primary" @click="copyToClipboard">{{this.url}}/{{this.roomId}}</button>
                  </template>
                  <a class="button is-primary is-inverted" @click="copyToClipboard">
                    <span class="icon"><i class="fas fa-copy"></i></span>
                    <span>{{ copyFeedback ? 'コピーしました！' : '共有リンクをコピー' }}</span>
                  </a>
                </VDropdown>
              </span>
              <span class="navbar-item feedback-note">
                <span class="feedback-note-pill">
                  <span class="icon feedback-note-icon"><i class="fas fa-bullhorn"></i></span>
                  <span class="feedback-note-text">皆さまの声をもとにアップデート中<br class="is-hidden-mobile">ご意見・ご要望をお気軽にお寄せください</span>
                </span>
              </span>
              <span class="navbar-item">
                <a class="button is-primary" href="https://forms.gle/4xckQvhxWuURB3um7" target="_blank" rel="noopener noreferrer">
                  <span class="icon"><i class="fas fa-comment"></i></span>
                  <span>ご意見・お問合せ</span>
                </a>
              </span>
            </div>
          </div>
        </div>
      </header>
      <div class="hero-body py-4">
        <p class="title is-4 is-spaced mb-2">
          Team poker
        </p>
        <p class="subtitle is-6 mb-0">
          WEBブラウザで簡単にプランニングポーカーなどの投開票をスムーズに実施できます。<br />
          <span class="is-size-7">Zoom/Meet/Teamsなど参加者全員とビデオ会議 or 音声通話している状態で利用することを前提としています。ルームを作成してリンクを共有するだけで利用可能で、作成したルームは最後の利用から1時間後に無効化されます。</span>
        </p>
      </div>
    </section>
    <section class="section">
      <div class="container">
        <!-- 入室前: 「作成」と「参加」の2導線を明確に分ける -->
        <template v-if="!roomId">
          <div class="columns is-vcentered is-centered">
            <div class="column is-5">
              <div class="box has-text-centered entry-card">
                <p class="heading has-text-grey mb-4">ホストの方</p>
                <button class="button is-info is-medium is-fullwidth" @click="setRoomId">
                  <span class="icon"><i class="fas fa-plus"></i></span>
                  <span>新しいルームを作成</span>
                </button>
                <p class="is-size-7 has-text-grey mt-4">ルームを作成すると共有用URLが発行されます。<br />それを参加者に共有しましょう。</p>
              </div>
            </div>
            <div class="column is-1 has-text-centered has-text-grey-light entry-or">
              または
            </div>
            <div class="column is-5">
              <div class="box entry-card">
                <p class="heading has-text-grey mb-4">招待された方</p>
                <div class="field">
                  <div class="control has-icons-left">
                    <input class="input is-medium" :class="[joinRoomError ? 'is-danger' : 'is-link is-light']" type="text" placeholder="ルームIDを入力" v-model="joinRoomId" @input="joinRoomError = ''">
                    <span class="icon is-left"><i class="fas fa-key"></i></span>
                  </div>
                  <p class="help is-danger" v-if="joinRoomError">{{ joinRoomError }}</p>
                </div>
                <div class="buttons">
                  <button class="button is-link is-light is-medium" @click="joinRoom(false)">
                    <span class="icon"><i class="fas fa-sign-in-alt"></i></span>
                    <span>入場して投票に参加</span>
                  </button>
                  <button class="button is-light is-medium" @click="joinRoom(true)">
                    <span class="icon"><i class="fas fa-eye"></i></span>
                    <span>観覧入場</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- 入室後: 共有リンクを目立たせ、自分の設定を分離 -->
        <template v-else>
          <!-- STEP1: 共有リンク（作成者のみ表示。参加者はリンク共有不要のため強調しない） -->
          <transition v-if="isHost" name="share-fade">
            <div v-if="!isShareCollapsed" class="box share-box mb-5">
              <div class="columns is-vcentered">
                <div class="column is-narrow is-hidden-mobile">
                  <span class="icon is-large has-text-link"><i class="fas fa-share-alt fa-2x"></i></span>
                </div>
                <div class="column">
                  <p class="is-size-7 has-text-grey mb-2">
                    <span class="tag is-link is-light mr-2">STEP 1</span>
                    <span>このリンクを参加者に共有してください</span>
                  </p>
                  <input class="input is-medium" type="text" :value="`${url}/${roomId}`" readonly @focus="$event.target.select()">
                </div>
                <div class="column is-narrow">
                  <button class="button is-link is-light is-medium is-fullwidth" @click="copyToClipboard">
                    <span class="icon"><i class="fas fa-copy"></i></span>
                    <span>リンクをコピー</span>
                  </button>
                  <p class="has-text-success has-text-centered is-size-7 mt-1" :class="{ 'is-invisible': !copyFeedback }">
                    <span class="icon is-small"><i class="fas fa-check"></i></span>
                    <span>コピーしました</span>
                  </p>
                </div>
              </div>
            </div>
            <div v-else class="level is-mobile mb-4 share-collapsed">
              <div class="level-left">
                <div class="level-item">
                  <span class="icon has-text-success"><i class="fas fa-check-circle"></i></span>
                  <span class="is-size-7 has-text-grey ml-1">リンクをコピーしました</span>
                </div>
              </div>
              <div class="level-right">
                <div class="level-item">
                  <button class="button is-small is-link is-light" @click="isShareCollapsed = false">
                    <span class="icon is-small"><i class="fas fa-share-alt"></i></span>
                    <span>共有リンクを再表示</span>
                  </button>
                </div>
              </div>
            </div>
          </transition>

          <!-- STEP2: 参加方法。参加確定後は「名前＋モード」だけの控えめなサマリにし、編集ボタンで再展開 -->
          <div class="box step2-box mb-5" :class="{ 'is-decided': !showJoinForm }">
            <!-- 参加確定後のサマリ表示（名前とモードのみ） -->
            <div v-if="!showJoinForm" class="level is-mobile mb-0">
              <div class="level-left">
                <div class="level-item">
                  <span class="tag is-medium" :class="isGhost ? 'is-dark' : (isOwner ? 'is-info' : 'is-info is-light')">
                    <span class="icon"><i class="fas" :class="isGhost ? 'fa-eye' : (isOwner ? 'fa-user-cog' : 'fa-user')"></i></span>
                    <span>{{ modeLabel }}</span>
                  </span>
                  <span v-if="myName" class="ml-3 has-text-weight-semibold">{{ myName }}</span>
                  <span class="is-size-7 has-text-grey ml-2">で参加中</span>
                </div>
              </div>
              <div class="level-right">
                <div class="level-item">
                  <button class="button is-small is-light" @click="isEditingJoin = true">
                    <span class="icon is-small"><i class="fas fa-pen"></i></span>
                    <span>編集</span>
                  </button>
                </div>
              </div>
            </div>

            <!-- 未参加 or 編集中は参加方法の入力フォームを表示 -->
            <template v-if="showJoinForm">
            <p class="is-size-7 has-text-grey mb-3 step2-subtitle">
              <span v-if="isHost" class="tag is-info is-light mr-2">STEP 2</span>
              <span>参加方法を選んでください</span>
            </p>
            <template v-if="!isGhost">
              <div class="level mb-2">
                <div class="level-left">
                  <div class="level-item">
                    <div class="buttons has-addons mb-0">
                      <button class="button" :class="[isOwner? '' : 'is-info is-selected']" @click="setOwner(false)">
                        <span class="icon is-small"><i class="fas fa-user"></i></span>
                        <span>メンバー</span>
                      </button>
                      <button class="button" :class="[isOwner? 'is-info is-selected' : '']" @click="setOwner(true)">
                        <span class="icon is-small"><i class="fas fa-user-cog"></i></span>
                        <span>オーナー</span>
                      </button>
                    </div>
                  </div>
                  <div class="level-item">
                    <form @submit.prevent="setName" class="field has-addons mb-0" :class="{ 'pulse-highlight': !name }">
                      <div class="control has-icons-left">
                        <input class="input is-info is-medium" type="text" autocomplete="nickname" placeholder="名前を入力" v-model="name">
                        <span class="icon is-left"><i class="fas fa-user"></i></span>
                      </div>
                      <div class="control">
                        <button class="button is-info is-medium" type="submit" :disabled="!name">
                          <span class="icon"><i class="fas fa-sign-in-alt"></i></span>
                          <span>参加</span>
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
                <div class="level-right">
                  <div class="level-item">
                    <button class="button is-light" @click="switchToGhost">
                      <span class="icon"><i class="fas fa-eye"></i></span>
                      <span>閲覧モードで参加</span>
                    </button>
                  </div>
                </div>
              </div>
              <p class="help has-text-grey">
                <b>オーナー</b>: 投票には参加しますが、投票中は自分の選択が画面に表示されません（票は集計されます／画面共有向け）。<br>
                <b>閲覧モード</b>: 自分は投票しませんが、選択肢の設定・投票の開始/終了などの進行操作は行えます。投票権を持たない進行役（オーナー）の方はこちらを選択してください。
              </p>
            </template>
            <template v-else>
              <div class="level">
                <div class="level-left">
                  <div class="level-item">
                    <span class="tag is-dark is-medium">
                      <span class="icon"><i class="fas fa-eye"></i></span>
                      <span>閲覧モードで参加中</span>
                    </span>
                  </div>
                </div>
                <div class="level-right">
                  <div class="level-item">
                    <button class="button is-info is-light" @click="switchToMember">
                      <span class="icon"><i class="fas fa-sign-in-alt"></i></span>
                      <span>投票に参加する</span>
                    </button>
                  </div>
                </div>
              </div>
              <p class="help has-text-grey">
                投票はしませんが、選択肢の設定や投票の開始/終了などの進行操作は行えます。
              </p>
            </template>
            </template>
          </div>
        </template>
        <template v-if="roomData && isJoined">
          <div class="tile is-parent">
            <VDropdown
              v-if="roomData.selectItems.length"
              class="tile is-12"
              :triggers="['hover', 'focus']"
              :offset="[0, 10]"
            >
              <template #popper>
                <span class="is-size-7 has-text-link">選択項目を変更できます。自由に項目を追加、削除することもできます。</span>
              </template>
              <button class="button is-info is-light is-fullwidth is-small" @click="openShowSettingModal" >
                <span class="icon is-small"><i class="fas fa-edit"></i></span>
                <span>選択項目変更</span>
              </button>
            </VDropdown>
            <div v-else class="tile is-12 is-vertical">
              <button class="button is-info is-light is-fullwidth is-large" :class="{ 'pulse-highlight': name }" @click="openShowSettingModal" >
                <span class="icon is-small"><i class="fas fa-edit"></i></span>
                <span>選択項目を設定する</span>
              </button>
              <p class="has-text-link has-text-centered is-size-7 mt-2">
                <span v-if="isHost" class="tag is-info is-light mr-1">STEP 3</span>
                投票の選択肢を設定してください。参加者全員が設定でき、途中で変更することも可能です。
              </p>
            </div>
          </div>
          <div class="modal" :class="[isShowSettingModal ? 'is-active' : '']">
            <div class="modal-background"></div>
            <div class="modal-content">
              <div class="box">
                <div class="buttons are-medium is-3">
                  <button @click="mode1220" class="tile button is-warning is-dark">
                    0.5, 1〜20
                  </button>
                  <button @click="modeFibonacci" class="tile button is-warning is-dark">
                    フィボナッチ
                  </button>
                  <button @click="modeSML" class="tile button is-warning is-dark">
                    Tシャツ
                  </button>
                  <button @click="modeTension" class="tile button is-warning is-dark">
                    気分
                  </button>
                  <button @click="modeOkrSelfDegree" class="tile button is-warning is-dark">
                    OKR自信度
                  </button>
                  <button @click="modeHighMediumLow" class="tile button is-warning is-dark">
                    優先度(高、中、低)
                  </button>
                  <button @click="modePlayingCards" class="tile button is-warning is-dark">
                    トランプ
                  </button>
                  <button @click="modeJanlen" class="tile button is-warning is-dark">
                    じゃんけん✊✌️🖐️
                  </button>
                </div>
                <div class="columns is-flex-direction-row is-flex-wrap-wrap">
                  <div
                    class="column is-3"
                    v-for="(customItem, index) in customItems"
                    :key="index"
                  >
                    <input class="input is-rounded" type="text" v-model="customItems[index]" />
                  </div>
                  <div class="column">
                    <button class="button is-ghost" @click="customAdd" >
                      <span class="icon is-small"><i class="fas fa-plus-square"></i></span>
                    </button>
                    <button class="button is-ghost" @click="customTake" >
                      <span class="icon is-small"><i class="fas fa-minus-square"></i></span>
                    </button>
                  </div>
                </div>
              </div>
              <div class="tile is-vertical">
                <button class="button is-primary is-light" @click="setSelectItems" >
                  <span class="icon is-small"><i class="fas fa-check"></i></span>
                  <span>適用</span>
                </button>
              </div>
            </div>
            <button class="modal-close is-large" aria-label="close" @click="closeShowSettingModal"></button>
          </div>
          <div class="tile is-parent">
            <template v-if="isTitleEdit">
              <form @submit.prevent="(e) => setTitle(e.target.title.value)" class="tile is-parent">
                <div class="tile is-10">
                  <input class="input is-medium" type="text" name="title" :value="roomData.title">
                </div>
                <div class="tile is-2">
                  <button class="button is-info is-light is-medium" type="submit">
                    <span class="icon"><i class="fas fa-pen"></i></span>
                  </button>
                </div>
              </form>
            </template>
            <template v-else>
              <VDropdown
                class="tile is-12"
                :triggers="['hover', 'focus']"
                :offset="[0, 10]"
              >
                <template #popper>
                  <span class="is-size-7 has-text-link">クリックでお題を変更できます。</span>
                </template>
                <button
                  class="tile is-12 button is-fullwidth"
                  :class="[roomData.title ? 'is-info is-large' : 'is-info is-outlined is-small']"
                  @click="() => isTitleEdit = !isTitleEdit"
                >
                  <span class="icon"><i class="fas fa-pen"></i></span>
                  <h2>{{ roomData.title ? roomData.title : 'お題を設定する' }}</h2>
                </button>
              </VDropdown>
            </template>
          </div>
          <div class="tile is-parent buttons are-medium">
            <template v-if="!roomData.isVoting">
              <VDropdown
                class="tile is-12"
                :triggers="['hover', 'focus']"
                :offset="[0, 10]"
              >
                <template #popper>
                  <span class="is-size-7 has-text-link">投票を開始します。</span>
                </template>
                <button v-if="!roomData.isVoting"
                  class="button is-primary is-light is-fullwidth"
                  :class="{ 'pulse-highlight': roomData.selectItems.length }"
                  @click="startVoting"
                  :disabled="!roomData.selectItems.length">
                  <span class="icon is-small"><i class="fas fa-hourglass-start"></i></span>
                  <span v-if="isHost" class="tag is-primary is-light mr-2">STEP 4</span>
                  <span>投票開始</span>
                </button>
              </VDropdown>
            </template>
            <template v-else>
              <VDropdown
                class="tile is-6"
                :triggers="['hover', 'focus']"
                :offset="[0, 10]"
              >
                <template #popper>
                  <span class="is-size-7 has-text-link">投票を締め切り、現在の投票結果を表示します。</span>
                </template>
                <button
                  class="button is-link is-light is-fullwidth"
                  @click="finishVoting"
                  :disabled="!roomData.selectItems.length">
                  <span class="icon is-small"><i class="fas fa-gavel"></i></span>
                  <span>投票締め切り</span>
                </button>
              </VDropdown>
              <VDropdown
                class="tile is-6"
                :triggers="['hover', 'focus']"
                :offset="[0, 10]"
              >
                <template #popper>
                  <span class="is-size-7 has-text-link">開始中の投票をキャンセルします。投票結果は表示されません。</span>
                </template>
                <button
                  class="button is-warning is-light is-fullwidth"
                  @click="cancelVoting"
                  :disabled="!roomData.selectItems.length">
                  <span class="icon is-small"><i class="fas fa-hourglass-start"></i></span>
                  <span>キャンセル</span>
                </button>
              </VDropdown>
            </template>
          </div>
          <template v-if="roomData.selectItems && roomData.selectItems.length">
            <div class="tile is-parent columns is-justify-content-flex-start buttons are-medium">
              <div class="column is-2"
                v-for="selectItem in roomData.selectItems"
                :key="selectItem"
              >
                <button @click="selectHandle"
                  class="button is-rounded is-fullwidth"
                  :value="selectItem"
                  :class="[roomData.isVoting ? 'is-primary' : '', isSelected(selectItem) ? 'is-light' : 'is-black']"
                  :disabled="!roomData.isVoting">
                  {{ selectItem }}
                </button>
              </div>
            </div>
          </template>
          <div class="columns" v-if="selectInfo">
            <template v-if="roomData.isJanken">
              <div class="column">
                <article class="message is-medium is-info">
                  <div class="message-header">
                    <p>勝ち : {{ selectInfo.max }}</p>
                  </div>
                </article>
              </div>
              <div class="column">
                <article class="message is-medium is-danger">
                  <div class="message-header">
                    <p>負け : {{ selectInfo.min }}</p>
                  </div>
                </article>
              </div>
            </template>
            <template v-else>
              <div class="column">
                <article class="message is-medium is-primary">
                  <div class="message-header">
                    <p>平均 : {{ selectInfo.average }}</p>
                  </div>
                </article>
              </div>
              <div class="column">
                <article class="message is-medium is-info">
                  <div class="message-header">
                    <p>最大 : {{ selectInfo.max }}</p>
                  </div>
                </article>
              </div>
              <div class="column">
                <article class="message is-medium is-danger">
                  <div class="message-header">
                    <p>最小 : {{ selectInfo.min }}</p>
                  </div>
                </article>
              </div>
            </template>
          </div>
          <div class="tile is-parent columns is-12 is-flex-direction-row is-flex-wrap-wrap">
            <template v-if="usersData">
              <div class="column is-2"
                v-for="(user) in usersData"
                :key="user.id"
              >
                <div class="notification is-12"
                  :class="userPanelClass(user)">
                  <VDropdown
                    :triggers="['hover', 'focus']"
                    :offset="[0, 10]"
                  >
                    <template #popper>
                      <span class="is-size-7 has-text-link"><strong>{{ user.name }}</strong>さんを退場させる</span>
                    </template>
                    <button class="delete" @click="confirmExitRoom(user)"></button>
                  </VDropdown>
                  <p class="subtitle is-6" :class="userPanelClass(user)">
                    {{ user.name ? user.name : '名前を設定中...' }}
                  </p>
                  <template v-if="roomData.isStart">
                    <template v-if="roomData.isVoting">
                      <template v-if="user.voting.id !== roomData.voting || !user.voting.selected">
                        <span class="button is-loading is-fullwidth" :class="userPanelClass(user)">voting</span>
                      </template>
                      <template v-else>
                        <span class="button is-fullwidth" :class="userPanelClass(user)">
                          <span class="icon"><i class="fas fa-pen-square"></i></span>
                        </span>
                      </template>
                    </template>
                    <template v-else-if="user.voting.id === roomData.voting">
                      <span class="title"
                        :class="userPanelClass(user)">
                        {{ user.voting.select }}
                      </span>
                    </template>
                  </template>
                </div>
              </div>
            </template>
          </div>
        </template>
      </div>
    </section>
  </div>
</template>

<script>
import roomUtil from '../common/roomUtil'

export default {
  name: 'PokerRoom',
  data: function () {
    const url = window.location.origin
    const joinRoomId = window.location.pathname.match(/\/(.+)$/) ? window.location.pathname.match(/\/(.+)$/)[1] : ''
    return {
      url,
      roomId: '',
      userId: '',
      name: '',
      isOwner: false,
      joinRoomId,
      custom: false,
      customItems: [1, 2, 3],
      joinRoomError: '',
      copyFeedback: false,
      isShowNotification: true,
      isShowSettingModal: false,
      roomData: null,
      usersData: null,
      isTitleEdit: false,
      isPolling: false,
      isGhost: false,
      selectInfo: null,
      isActiveNavbarMenu: false,
      isShareCollapsed: false,
      isHost: false,
      isEditingJoin: false,
    }
  },
  emits: ['rewrite'],
  beforeUnmount() {
    clearTimeout(this._copyFeedbackTimer)
    clearTimeout(this._shareCollapseTimer)
  },
  computed: {
    // 名前を決定して参加済み、または閲覧モードで参加済みかどうか
    isJoined: function () {
      if (this.isGhost) return true
      if (!this.usersData) return false
      const me = this.usersData.find(user => user.id === this.userId)
      return !!(me && me.name)
    },
    // 参加方法の入力フォームを表示するか（未参加 or 編集中）
    showJoinForm: function () {
      return !this.isJoined || this.isEditingJoin
    },
    // 自分の表示名
    myName: function () {
      if (!this.usersData) return ''
      const me = this.usersData.find(user => user.id === this.userId)
      return me ? me.name : ''
    },
    // 現在のモード表示名
    modeLabel: function () {
      if (this.isGhost) return '閲覧モード'
      return this.isOwner ? 'オーナー' : 'メンバー'
    },
  },
  methods: {
    setRoomId: async function () {
      const response = await roomUtil.createRoom()
      this.roomId = response.roomId
      this.userId = response.userId
      this.isOwner = true
      this.isHost = true
      history.replaceState('','',`/${this.roomId}`);
      // 作成直後から部屋のセットアップ画面（選択項目・投票開始など）を表示する
      this.setRoomData(response.roomData)
      this.polling()
    },
    setRoomData: function (roomDataString) {
      const roomData = roomDataString
      this.roomData = roomData
    },
    joinRoom: async function (isGhost) {
      const response = await roomUtil.joinRoom(this.joinRoomId, isGhost)
      if (response) {
        this.roomId = this.joinRoomId
        this.userId = response.userId
        if (isGhost) {
          this.isGhost = isGhost
          this.userId = 'ghost'
          this.setRoomData(response.roomData)
          this.polling()
        }
      } else {
        this.joinRoomError = 'ルームが存在しません。IDを確認してください。'
      }
    },
    setName: async function () {
      const response = await roomUtil.setName(this.name)
    
      if (response) {
        this.setRoomData(response.roomData)
        this.isEditingJoin = false
        this.polling()
      }
    },
    setOwner: function(value) {
      this.isOwner = value
    },
    switchToGhost: async function () {
      if (!confirm('閲覧モードに切り替えますか？投票への参加が解除され、再度参加する際は名前の再設定が必要になります。')) return
      // 先にローカル状態を閲覧モードへ更新し、user削除の同期でリダイレクトされるのを防ぐ
      this.isGhost = true
      this.userId = 'ghost'
      this.isOwner = false
      this.name = ''
      this.isEditingJoin = false
      this.polling()
      await roomUtil.switchToGhost()
    },
    switchToMember: async function () {
      const response = await roomUtil.switchToMember()
      if (response) {
        this.isGhost = false
        this.userId = response.userId
        this.name = ''
        this.isEditingJoin = false
        this.polling()
      }
    },
    setSelectItems: async function () {
      const selectItems = this.customItems.filter(item => item)
      const response = await roomUtil.setSelectItems(selectItems)
      if (response) {
        this.setRoomData(response.roomData)
        this.closeShowSettingModal()
      }
    },
    selectHandle: async function (e) {
      if (this.isGhost) return
      const response = await roomUtil.setSelected(e.target.value)
      if (response) {
        this.setRoomData(response.roomData)
      }
    },
    isSelected: function(value) {
      if (this.isGhost || (this.isOwner && this.roomData.isVoting) || !this.usersData) return false
      const userData = this.usersData.find(userData => userData.id === this.userId)
      if (!userData || !userData.voting || userData.voting.id !== this.roomData.voting) return false
      return userData.voting.select == value
    },
    copyToClipboard: function () {
      const urlLink = `${this.url}/${this.roomId}`
      navigator.clipboard.writeText(urlLink)
      this.copyFeedback = true
      clearTimeout(this._copyFeedbackTimer)
      this._copyFeedbackTimer = setTimeout(() => { this.copyFeedback = false }, 2000)
      // コピー後は数秒で共有バナーを畳み、次のステップを強調する
      clearTimeout(this._shareCollapseTimer)
      this._shareCollapseTimer = setTimeout(() => { this.isShareCollapsed = true }, 2500)
    },
    customAdd: function () {
      let addItem = 0
      if (this.customItems.length) {
        const lastOneBefore = Number(this.customItems[this.customItems.length - 2])
        const lastOne = Number(this.customItems[this.customItems.length - 1])
        addItem = lastOneBefore && lastOne ? (lastOne + lastOne - lastOneBefore) : (lastOne ? lastOne + 1 : '')
      }
      this.customItems = [...this.customItems, addItem]
    },
    customTake: function () {
      if (!this.customItems.length) return
      const tmp = [...this.customItems]
      this.customItems = tmp.slice(0, -1)
    },
    mode1220: function () {
      this.customItems = [0.5, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, '☕️']
    },
    modeTension: function () {
      this.customItems = ['Extra best', 'Best', 'Good', 'Usually', 'Bad', 'Worst']
      this.setTitle('気分')
    },
    modeSML: function () {
      this.customItems = ['S', 'M', 'L', 'XL']
    },
    modePlayingCards: function () {
      this.customItems = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, '11: Jack', '12: Queen', '13: King', '?: Joker']
    },
    modeFibonacci: function () {
      const fib = (n) => {
        let a = 1
        let b = 1
        for (let i = 3; i <= n; i++) {
          const c = a + b
          a = b
          b = c
        }
        return b
      }
      const arr = ['0', '0.5']
      for (let i = 2; i < 13; i++) {
        arr.push(fib(i))
      }
      arr.push('？')
      arr.push('☕️')
      this.customItems = arr
    },
    modeOkrSelfDegree: function () {
      this.customItems = ['5: Goalが見えそう', '4: 快調に進行中', '3: 進捗あり', '2: 停滞中', '1: むしろ後退']
      this.setTitle('OKR自信度')
    },
    modeHighMediumLow: function () {
      this.customItems = ['3: 高', '2: 中', '1: 低']
      this.setTitle('優先度')
    },
    modeJanlen: function () {
      this.customItems = ['✊: ぐー', '✌️: ちょき', '🖐️: ぱー']
      this.setTitle('じゃんけん✊✌️🖐️')
    },
    hideNotification: function () {
      this.isShowNotification = false
    },
    openShowSettingModal: function () {
      this.isShowSettingModal = true
    },
    closeShowSettingModal: function () {
      this.isShowSettingModal = false
    },
    startVoting: async function () {
      await roomUtil.startVoting()
    },
    finishVoting: async function () {
      await roomUtil.finishVoting()
    },
    cancelVoting: async function () {
      await roomUtil.cancelVoting()
    },
    setTitle: async function (title) {
      const response = await roomUtil.setTitle(title || '')
      if (response) {
        this.setRoomData(response.roomData)
        this.isTitleEdit = false
      }
    },
    getNumber: (target) => {
      const s = `${target}`.match(/(\d+\.\d+|\d+)[^\d]?.*$/)
      return s ? Number(s[1]) : null
    },
    calSelect: function () {
      if (this.roomData.isVoting || !this.roomData.voting || !this.usersData || !this.usersData.length) {
        this.selectInfo = null
        return
      }
      const selectNumbers = []
      for (const user of Object.values(this.usersData)) {
        if (this.roomData.isJanken) {
          selectNumbers.push(user.voting.select)
          continue
        } 
        const n = this.getNumber(user.voting.select)
        if (n === null || !user.voting.selected || user.voting.id !== this.roomData.voting) {
          this.selectInfo = null
          continue
        }
        selectNumbers.push(n)
      }
      if (!selectNumbers.length) {
        this.selectInfo = null
        return
      }
      if (this.roomData.isJanken) {
        const gu = selectNumbers.filter(e => e.match(/✊/))
        const choki = selectNumbers.filter(e => e.match(/✌️/))
        const par = selectNumbers.filter(e => e.match(/🖐️/))
        if (gu.length && choki.length && !par.length) {
            this.selectInfo = {
            average: null,
            max: gu[0],
            min: choki[0]
          }
        } else if (!gu.length && choki.length && par.length) {
            this.selectInfo = {
            average: null,
            max: choki[0],
            min: par[0]
          }
        } else if (gu.length && !choki.length && par.length) {
            this.selectInfo = {
            average: null,
            max: par[0],
            min: gu[0]
          }
        }

      } else {
        this.selectInfo = {
          average: selectNumbers.reduce((acc, cur) => acc + cur) / selectNumbers.length,
          max: selectNumbers.reduce((acc, cur) => acc > cur ? acc : cur),
          min: selectNumbers.reduce((acc, cur) => acc < cur ? acc : cur)
        }
      }
    },
    confirmExitRoom: function (user) {
      const displayName = user.name || '名前未設定のユーザー'
      if (confirm(`${displayName} さんを退場させますか？`)) {
        roomUtil.exitRoom(user.id)
      }
    },
    polling: async function () {
      if (this.isPolling) return
      this.isPolling = true
      roomUtil.setSyncFunction(this.syncFunctionRoomData, this.syncFunctionUsersData)
    },
    syncFunctionRoomData: function(roomData) {
      if (roomData) {
        this.roomData = roomData
        this.calSelect()
      } else {
        window.open(this.url, '_top')
      }
    },
    syncFunctionUsersData: function(usersData) {
      if (this.isGhost || usersData.find(user => user.id === this.userId)) {
        this.usersData = usersData
        this.calSelect()
      } else {
        window.open(this.url, '_top')
      }
    },
    userPanelClass: function (user) {
      const classList = []
      if (this.selectInfo) {
        if (this.roomData.isJanken) {
          if (user.voting.select === this.selectInfo.max) classList.push('is-info')
          else if (user.voting.select === this.selectInfo.min) classList.push('is-danger')
          else classList.push('is-warning')
        } else {
          if (this.getNumber(user.voting.select) === this.selectInfo.max) classList.push('is-info')
          else if (this.getNumber(user.voting.select) === this.selectInfo.min) classList.push('is-danger')
          else classList.push('is-warning')
        }
      } else {
        classList.push('is-warning')
      }
      return classList
    },
    activeNavbarMenu: function() {
      this.isActiveNavbarMenu = !this.isActiveNavbarMenu
    }
  }
}
</script>

<style scoped>
.entry-card {
  height: 100%;
}
.entry-or {
  font-weight: 700;
}
.share-box {
  border: 1px solid #dbe2f5;
  background-color: #f7f9ff;
}
.step2-box {
  border: 1px solid #ededed;
  transition: background-color 0.25s ease, box-shadow 0.25s ease, padding 0.25s ease;
}
/* 参加が確定したら名前＋モードだけの控えめなサマリにし、高さを抑える */
.step2-box.is-decided {
  padding: 0.6rem 1rem;
  background-color: #fafafa;
  border-color: #f0f0f0;
  box-shadow: none;
}
/* ヘッダーのお問合せ横に添える控えめな一言 */
.feedback-note .feedback-note-pill {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.3rem 0.85rem;
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 9999px;
  background-color: rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.92);
  line-height: 1.25;
}
.feedback-note .feedback-note-icon {
  color: rgba(255, 255, 255, 0.85);
}
.feedback-note .feedback-note-text {
  font-size: 0.72rem;
  letter-spacing: 0.02em;
}

/* 共有バナーの開閉アニメーション */
.share-fade-enter-active,
.share-fade-leave-active {
  transition: opacity 0.4s ease, transform 0.4s ease;
}
.share-fade-enter-from,
.share-fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

/* 次のステップを強調するパルス */
.pulse-highlight {
  animation: pulse-highlight 1.6s ease-out 3;
}
@keyframes pulse-highlight {
  0% {
    box-shadow: 0 0 0 0 rgba(72, 95, 199, 0.5);
  }
  70% {
    box-shadow: 0 0 0 12px rgba(72, 95, 199, 0);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(72, 95, 199, 0);
  }
}
</style>
